import type {RouteLocationNormalized, RouteLocationResolvedGeneric} from 'vue-router';
import {defineNuxtRouteMiddleware, useAuth, useNuxtApp} from '#imports';
import {safelyNavigateTo} from '@malt/utils';
import type {OrganizationMemberDetails, UserIdentity} from '../models';
import type {Logger} from '#malt/nuxt-logger-module';

/**
 * Determines which permission check to use based on the URL path
 */
function getPermissionCheck(
    path: string,
):
    | keyof Pick<
          OrganizationMemberDetails,
          'userMaySeeReporting' | 'userMaySeeInvoicing' | 'userMaySeeFreelancerCompliance' | 'userMayEditLegalEntityDetails'
      >
    | null {
    if (path.startsWith('/dashboard-client/analytics')) {
        return 'userMaySeeReporting';
    }
    if (path.startsWith('/invoicing/company')) {
        return 'userMaySeeInvoicing';
    }
    if (path.startsWith('/freelancer/company/workforce')) {
        return 'userMaySeeFreelancerCompliance';
    }
    return null;
}

/**
 * Determines if user has access to the organization page
 */
function hasUserAccess(
    store: ReturnType<typeof useAuth>,
    permissionCheck: keyof Pick<
        OrganizationMemberDetails,
        'userMaySeeReporting' | 'userMaySeeInvoicing' | 'userMaySeeFreelancerCompliance' | 'userMayEditLegalEntityDetails'
    >,
    path: string,
): boolean {
    // 1. Check if user has ROLE_ADMIN - if yes, allow access
    const hasAdminRole: boolean = store.hasEnoughPrivilege(['ROLE_ADMIN']);
    const currentIdentity = store.getSelectedIdentity;

    if (hasAdminRole) {
        return true;
    }

    // Check if user is admin and NOT accessing legal-entity/organization settings
    const isLegalEntityOrOrgSettingsPath = path.startsWith('/client-settings/legal-entity/') || path.startsWith('/client-settings/organization/');

    if (!currentIdentity) {
        return false;
    }

    if (currentIdentity?.admin && !isLegalEntityOrOrgSettingsPath) {
        return true;
    }

    if (!currentIdentity.organizationId) {
        return false;
    }

    const organizationMemberDetails = store.getOrganizationMemberDetails(currentIdentity.organizationId);
    return organizationMemberDetails?.[permissionCheck] === true;
}

/**
 * Middleware to restrict access to organisation pages (analytics, invoicing, freelancer-workforce).
 *
 * Access is granted if:
 * 1. User has ROLE_ADMIN privilege, OR
 * 2. user has the specific permission in organizationMemberDetails
 */
export default defineNuxtRouteMiddleware((to: RouteLocationNormalized) => {
    const nuxtApp = useNuxtApp();
    const {$pinia} = nuxtApp;
    const logger = nuxtApp.$logger as Logger;

    const href = 'href' in to ? (to.href as string) : to.fullPath;

    const store = useAuth($pinia);

    const permissionCheck = getPermissionCheck(href);

    if (!permissionCheck) {
        return;
    }

    try {
        const userHasAccess = hasUserAccess(store, permissionCheck, href);

        if (!userHasAccess) {
            return safelyNavigateTo('/', {externalToThisApp: true});
        }
    } catch (error) {
        logger.error(`Error in organisation-pages-access middleware: ${error}`, error);
        return safelyNavigateTo('/', {externalToThisApp: true});
    }
});

export {getPermissionCheck};
